import clsx from 'clsx';
import { Badge } from '../../../components/ui/Badge';
import { type AdsStage, type BucketStatus, type Flag, type Language, type Rep, type Stage } from '../../../api/pilots';

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

/** One meaning per stage; the hint is the badge's tooltip. */
export const STAGE_META: Record<Stage, { label: string; badge: BadgeVariant; hint: string }> = {
  not_started:    { label: 'Not started',    badge: 'default', hint: 'No touch yet' },
  in_sequence:    { label: 'In sequence',    badge: 'info',    hint: 'At least one touch in lemlist, or a call ticked by hand' },
  replied:        { label: 'Replied',        badge: 'warning', hint: 'Replied, not yet marked interested or not' },
  not_interested: { label: 'Not interested', badge: 'error',   hint: 'Marked not interested in lemlist' },
  interested:     { label: 'Interested',     badge: 'success', hint: 'Marked interested in lemlist' },
  meeting:        { label: 'Meeting booked', badge: 'success', hint: 'A booking on their email, or ticked by hand' },
};

export function StageBadge({ stage }: { stage: Stage }) {
  return <span title={STAGE_META[stage].hint}><Badge variant={STAGE_META[stage].badge}>{STAGE_META[stage].label}</Badge></span>;
}

/** "Jovana Stojanovic · SDR", for pickers. */
export function repLabel(rep: Rep): string {
  return rep.role ? `${repName(rep)} · ${rep.role.toUpperCase()}` : repName(rep);
}

export function repName(rep: Rep | null): string {
  if (!rep) return 'Unassigned';
  return rep.name ?? rep.email.split('@')[0] ?? rep.email;
}

export function flagText(flag: Flag): string {
  switch (flag.kind) {
    case 'other_sender':
      return `Touched by ${flag.senders.join(', ')}, not the assigned rep`;
    case 'not_started':
      return `Imported ${flag.days} days ago, still in no lemlist campaign`;
    case 'failures':
      return `${flag.count} lemlist failure${flag.count === 1 ? '' : 's'}${flag.lastReason ? `: ${flag.lastReason}` : ''}`;
    case 'wrong_language':
      return `${LANGUAGE_META[flag.language].label}, but ${flag.rep} is an SDR who writes English only: give to an AE`;
  }
}

/** Human label for a timeline row. */
export const ACTIVITY_LABELS: Record<string, string> = {
  linkedinInviteDone: 'LinkedIn invite sent',
  linkedinInviteAccepted: 'LinkedIn invite accepted',
  linkedinSent: 'LinkedIn message',
  linkedinVoiceNoteDone: 'LinkedIn voice note',
  emailsSent: 'Email sent',
  aircallEnded: 'Call',
  aircallDone: 'Call',
  manualDone: 'Manual task done',
  emailsReplied: 'Replied by email',
  linkedinReplied: 'Replied on LinkedIn',
  linkedinInterested: 'Marked interested',
  emailsInterested: 'Marked interested',
  manualInterested: 'Marked interested',
  linkedinNotInterested: 'Marked not interested',
  emailsNotInterested: 'Marked not interested',
  linkedinInviteFailed: 'LinkedIn invite failed',
  linkedinSendFailed: 'LinkedIn message failed',
  linkedinSendPendingInvitation: 'Message held: invite not accepted yet',
  linkedinVisitFailed: 'Profile visit failed',
  linkedinVoiceNoteFailed: 'Voice note failed',
  emailsFailed: 'Email failed',
  emailsBounced: 'Email bounced',
};

export const BUCKET_STATUS_META: Record<BucketStatus, { label: string; badge: BadgeVariant }> = {
  planned: { label: 'Planned', badge: 'default' },
  active: { label: 'Active', badge: 'success' },
  done: { label: 'Done', badge: 'default' },
};

export function BucketStatusBadge({ status }: { status: BucketStatus }) {
  return <Badge variant={BUCKET_STATUS_META[status].badge}>{BUCKET_STATUS_META[status].label}</Badge>;
}

export const ADS_STAGE_META: Record<AdsStage, { label: string; badge: BadgeVariant }> = {
  not_briefed: { label: 'Not briefed', badge: 'default' },
  briefed: { label: 'Briefed', badge: 'warning' },
  created: { label: 'Created, not live', badge: 'info' },
  published: { label: 'Published', badge: 'success' },
};

export function AdsStageBadge({ stage }: { stage: AdsStage }) {
  return <Badge variant={ADS_STAGE_META[stage].badge}>{ADS_STAGE_META[stage].label}</Badge>;
}

/**
 * "42%"; empty when there is no base (callers only show it with people in the
 * bucket). A share that is not zero never reads "0%": 1 of 400 is "<1%".
 */
export function pct(n: number, total: number): string {
  if (total <= 0) return '';
  const rounded = Math.round((n / total) * 100);
  return rounded === 0 && n > 0 ? '<1%' : `${rounded}%`;
}

/** Outreach language: who writes it. SDRs write English only; the AEs cover the rest. */
export const LANGUAGE_META: Record<Language, { label: string; short: string; who: string }> = {
  en:    { label: 'English',        short: 'EN',    who: 'SDR' },
  nl:    { label: 'Dutch',          short: 'NL',    who: 'AE' },
  fr:    { label: 'French',         short: 'FR',    who: 'AE' },
  de:    { label: 'German',         short: 'DE',    who: 'AE' },
  es:    { label: 'Spanish',        short: 'ES',    who: 'AE' },
  nl_fr: { label: 'Dutch or French', short: 'NL/FR', who: 'AE, pick the language' },
};

export function LanguageBadge({ language }: { language: Language }) {
  const meta = LANGUAGE_META[language];
  return (
    <span
      className={clsx(
        'inline-flex rounded px-1.5 py-0.5 text-body-xs font-medium tabular-nums',
        // A language is a category, not a status: neutral, except "pick one" which needs a decision.
        language === 'en' ? 'bg-surface-tertiary text-text-tertiary' : language === 'nl_fr' ? 'bg-warning-light text-warning-dark' : 'bg-surface-sunken text-text-secondary',
      )}
      title={`${meta.label}: ${meta.who}`}
    >
      {meta.short}
    </span>
  );
}

/** Legal-form endings that mark a name as a company, not a person ("Absolute Ltd", "Acme B.V."). */
const COMPANY_SUFFIX = /\b(ltd|limited|llc|inc|corp|plc|gmbh|ag|bv|b\.v|nv|n\.v|s\.a|sas|sarl|srl|s\.p\.a|a\/s|aps|pty)\.?$/i;

/**
 * True when a "person" row is really a company: its name carries a legal form or equals the company's.
 * Happens when a LinkedIn company page or a generic inbox (main@, info@) is imported as a person.
 */
export function isCompanyAsPerson(fullName: string, companyName?: string | null): boolean {
  const name = fullName.trim();
  if (!name) return false;
  if (companyName && name.toLowerCase() === companyName.trim().toLowerCase()) return true;
  return COMPANY_SUFFIX.test(name);
}

/** A person's name, or — when the row is really a company — the company name styled as one, with a muted note. */
export function PersonName({ fullName, companyName }: { fullName: string; companyName?: string | null }) {
  if (!isCompanyAsPerson(fullName, companyName)) return <>{fullName}</>;
  return (
    <span title="Imported as a person, but the name is a company's. Find the named contact before reaching out.">
      <span className="font-normal text-text-secondary">{fullName}</span>
      <span className="ml-1.5 text-body-xs font-normal text-text-tertiary">company, no named person</span>
    </span>
  );
}
